import { CTAButton } from "./CTAButton";
import type { TextComposerCtaButton } from "./ctaSchema";
import {
  ctaButtonSchema,
  validateCtaButton,
  isCtaButtonValid,
} from "./ctaSchema";
import type { TextComposerExtension } from "../../editor/extensionContract";
import { transformTextComposerMergeTags } from "../mergeTags/mergeTagDefinitions";
import type { ProductGridModelValue } from "@/features/EmailEditor/plugins/productGridConstants";

declare module "../../editor/authoringState" {
  interface TextComposerAuthoringState {
    ctaButton?: TextComposerCtaButton;
  }
}

const CTA_OWNED_KEY = "ctaButton";

function formatCtaLine(
  buttonText: string | undefined,
  buttonUrl: string | undefined,
  productModel: ProductGridModelValue,
): string {
  const trimmedText = transformTextComposerMergeTags(
    buttonText?.trim() ?? "",
    productModel,
  );
  const trimmedUrl = transformTextComposerMergeTags(
    buttonUrl?.trim() ?? "",
    productModel,
  );

  if (trimmedText.length > 0 && trimmedUrl.length > 0) {
    return `${trimmedText} - ${trimmedUrl}`;
  }

  return trimmedText || trimmedUrl;
}

function isCtaSliceEmpty(value: TextComposerCtaButton | undefined): boolean {
  if (!value) return true;
  return !value.buttonText?.trim() && !value.buttonUrl?.trim();
}

export const ctaExtension: TextComposerExtension<TextComposerCtaButton> = {
  id: "cta",

  stateSchema: ctaButtonSchema,

  isEmpty: isCtaSliceEmpty,

  persistence: {
    ownedKeys: [CTA_OWNED_KEY],
    serialize: (slice) => ({ [CTA_OWNED_KEY]: slice }),
    deserialize: (raw) => {
      const candidate = raw[CTA_OWNED_KEY];
      if (candidate === undefined) return undefined;
      const parsed = ctaButtonSchema.safeParse(candidate);
      return parsed.success ? parsed.data : undefined;
    },
  },

  createActions: (set, get) => ({
    setCtaButton(updates: Partial<TextComposerCtaButton>) {
      set((state) => {
        const current = get().authoringState.ctaButton;
        const merged: TextComposerCtaButton = {
          buttonText: current?.buttonText ?? "",
          buttonUrl: current?.buttonUrl ?? "",
          ...(updates as TextComposerCtaButton),
        };
        if (
          merged.buttonText.trim().length === 0 &&
          merged.buttonUrl.trim().length === 0
        ) {
          delete state.authoringState.ctaButton;
        } else {
          state.authoringState.ctaButton = merged;
        }
      });
    },
  }),

  contentContribution: (state) => {
    const cta = state.ctaButton;
    if (!cta) return null;
    if (!cta.buttonText.trim() && !cta.buttonUrl.trim()) return null;
    return { text: true };
  },

  transformSms: (state, payload, context) => {
    const ctaLine = formatCtaLine(
      state.ctaButton?.buttonText,
      state.ctaButton?.buttonUrl,
      context.productModel as ProductGridModelValue,
    );
    if (!ctaLine) return payload;

    const currentText = payload.content.body.text;
    const text =
      currentText.length > 0 ? `${currentText}\n\n${ctaLine}` : ctaLine;

    return { content: { body: { type: "TEXT", text } } };
  },

  transformRcs: (state, payload, context) => {
    const buttonText = transformTextComposerMergeTags(
      state.ctaButton?.buttonText.trim() ?? "",
      context.productModel as ProductGridModelValue,
    );
    const buttonUrl = transformTextComposerMergeTags(
      state.ctaButton?.buttonUrl.trim() ?? "",
      context.productModel as ProductGridModelValue,
    );

    if (buttonText.length > 0 && buttonUrl.length > 0) {
      return {
        content: {
          ...payload.content,
          buttons: [
            ...((
              payload.content as {
                buttons?: { type: "OPEN_URL"; text: string; url: string }[];
              }
            ).buttons ?? []),
            { type: "OPEN_URL" as const, text: buttonText, url: buttonUrl },
          ],
        },
      };
    }

    return payload;
  },

  transformMms: (state, payload, context) => {
    const ctaLine = formatCtaLine(
      state.ctaButton?.buttonText,
      state.ctaButton?.buttonUrl,
      context.productModel as ProductGridModelValue,
    );
    if (!ctaLine) return payload;

    const body = payload.content.body;
    if (body.type === "TEXT") {
      const text =
        body.text.length > 0 ? `${body.text}\n\n${ctaLine}` : ctaLine;
      return { content: { body: { type: "TEXT", text } } };
    }

    if (body.type === "IMAGE") {
      const existingText = "text" in body ? (body.text ?? "") : "";
      const text =
        existingText.length > 0 ? `${existingText}\n\n${ctaLine}` : ctaLine;
      return {
        content: { body: { ...body, text } },
      };
    }

    return payload;
  },

  validate: (state) => {
    if (!state.ctaButton) return { valid: true };
    if (!isCtaButtonValid(state.ctaButton)) {
      const errors = validateCtaButton(state.ctaButton);
      return { valid: false, errors };
    }
    return { valid: true };
  },

  component: CTAButton,
};
